import { useState, useEffect, useRef, useCallback } from 'react';
import localforage from 'localforage';
import { usePyrenzAlert } from '@renderer/provider';
import { Utils } from '@client/utility';
import { memoryOptimizer } from '@client/utility';

export type Role = 'system' | 'user' | 'char';

export interface ChatMessage {
  id?: string;
  role: Role;
  text?: string;
  content?: string;
  chunks?: string[];
  alternative_messages?: string[];
  current?: string;
  isGeneratingEmptyCharMessage?: boolean;
  isGenerate?: boolean;
  name?: string;
}

const store = localforage.createInstance({
  name: 'ChatDatabase',
  storeName: 'chat_messages',
});

export function useAssistantAPI() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [contextInput, setContextInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastCharacterData, setLastCharacterData] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const showAlert = usePyrenzAlert();

  const loadInitialMessages = async () => {
    const stored = await store.getItem<ChatMessage[]>('all_messages');
    if (stored?.length) return setMessages(stored);
    let instructionText = '';
    try {
      const res = await fetch('/Markdowns/instruction.md');
      if (res.ok) instructionText = await res.text();
    } catch (err) {}
    const systemMsg: ChatMessage = {
      role: 'system',
      text: instructionText || 'Hello! I can help you create characters.',
    };
    const charMsg: ChatMessage = {
      role: 'char',
      text: 'Hello! I can help you create characters. First, can you tell me what the character is?',
      chunks: [
        'Hello! I can help you create characters. First, can you tell me what the character is?',
      ],
    };
    const initialMessages = [systemMsg, charMsg];
    setMessages(initialMessages);
    await store.setItem('all_messages', initialMessages);
  };

  useEffect(() => {
    loadInitialMessages();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const saveMessages = async (allMessages: ChatMessage[]) => {
    await store.setItem('all_messages', allMessages);
    memoryOptimizer.optimize();
  };

  const mapRoleForAPI = (role: Role) => (role === 'char' ? 'assistant' : role);

  const extractCharacterData = useCallback((text: string): any | null => {
    const jsonMatch = text.match(/```json\n([\s\S]*?)\n```/);
    if (jsonMatch) {
      try {
        return JSON.parse(jsonMatch[1]);
      } catch {
        return null;
      }
    }
    
    const curlyMatch = text.match(/\{[\s\S]*"name"[\s\S]*\}/);
    if (curlyMatch) {
      try {
        return JSON.parse(curlyMatch[0]);
      } catch {
        return null;
      }
    }
    
    return null;
  }, []);

  const handleSend = async (text: string) => {
    if (!text.trim()) return;
    const userMessage: ChatMessage = { role: 'user', text };
    const allMessages: ChatMessage[] = [...messages, userMessage];
    const typingIndicator: ChatMessage = {
      role: 'char',
      chunks: [],
      alternative_messages: [],
      current: '',
      isGeneratingEmptyCharMessage: true,
      isGenerate: true,
    };
    const localMessages = [...allMessages, typingIndicator];
    setMessages(localMessages);
    setInput('');
    setLoading(true);
    await saveMessages(localMessages);
    try {
      const response = await fetch('https://text.pollinations.ai/ ', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'mistral',
          messages: allMessages.map((m) => ({
            role: mapRoleForAPI(m.role),
            content: m.text ?? m.content ?? '',
          })),
          temperature: 0.7,
          stream: true,
          private: true,
        }),
      });
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader available');
      let buffer = '';
      const decoder = new TextDecoder();
      let fullResponse = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const dataStr = trimmed.replace(/^data:\s*/, '');
          if (dataStr === '[DONE]') continue;
          try {
            const parsed = JSON.parse(dataStr);
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) {
              fullResponse += delta;
              setMessages((prev) => {
                const updated = [...prev];
                const index = updated.findIndex(
                  (m) => m.isGeneratingEmptyCharMessage
                );
                if (index !== -1) {
                  const target = updated[index];
                  const newChunks = [...(target.chunks || []), delta];
                  updated[index] = {
                    ...target,
                    chunks: newChunks,
                    current: newChunks.join(''),
                  };
                }
                return [...updated];
              });
            }
          } catch {}
        }
        memoryOptimizer.optimize();
      }
      
      const characterData = extractCharacterData(fullResponse);
      if (characterData) {
        setLastCharacterData(characterData);
        showAlert('Character data detected! Form will be filled automatically.', 'success');
      }
      
      setMessages((prev) => {
        const updated = [...prev];
        const index = updated.findIndex((m) => m.isGeneratingEmptyCharMessage);
        if (index !== -1) {
          const target = updated[index];
          updated[index] = {
            ...target,
            isGeneratingEmptyCharMessage: false,
            isGenerate: false,
            text: target.current ?? target.chunks?.join('') ?? '',
          };
        }
        saveMessages(updated);
        return updated;
      });
    } catch {
      showAlert('Could not fetch response from model', 'error');
      setMessages((prev) => {
        const updated = [...prev];
        const index = updated.findIndex((m) => m.isGeneratingEmptyCharMessage);
        if (index !== -1)
          updated[index] = {
            role: 'char',
            text: 'Error: could not fetch response.',
            chunks: ['Error: could not fetch response.'],
            isGenerate: false,
          };
        saveMessages(updated);
        return updated;
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    let instructionText = '';
    try {
      const res = await fetch('/Markdowns/instruction.md');
      if (res.ok) instructionText = await res.text();
    } catch {}
    const systemMsg: ChatMessage = {
      role: 'system',
      text: instructionText || 'Hello! I can help you create characters.',
    };
    const charMsg: ChatMessage = {
      role: 'char',
      text: 'Hello! I can help you create characters. First, can you tell me what the character is?',
      chunks: [
        'Hello! I can help you create characters. First, can you tell me what the character is?',
      ],
    };
    const initialMessages = [systemMsg, charMsg];
    setMessages(initialMessages);
    setLastCharacterData(null);
    await store.setItem('all_messages', initialMessages);
    memoryOptimizer.clearTraces();
    showAlert('Chat cleared', 'success');
  };

  const handleContextSubmit = async () => {
    if (!contextInput.trim()) return;
    try {
      const { data, error } = await Utils.db.functions.invoke('site-scraper', {
        body: { context: contextInput },
      });
      if (error) {
        showAlert('Error submitting context', 'error');
        return;
      }
      if (data?.result) {
        setMessages((prev) => {
          const systemMsg: ChatMessage = {
            role: 'system',
            text: `[Context]\n${data.result}`,
          };
          const updated = [systemMsg, ...prev];
          saveMessages(updated);
          return updated;
        });
        showAlert('Context successfully added to memory!', 'success');
      } else {
        showAlert('No data returned from context function', 'error');
      }
      setContextInput('');
    } catch {
      showAlert('Error submitting context', 'error');
    }
  };

  const handleExtract = () => {
    const json = JSON.stringify(messages, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conversation-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return {
    messages,
    input,
    setInput,
    contextInput,
    setContextInput,
    loading,
    handleSend,
    handleClear,
    handleContextSubmit,
    handleExtract,
    messagesEndRef,
    lastCharacterData,
  };
}